import { Col, ConfigProvider, DatePicker, Form, Input, Row, Select, Table } from "antd";
import React, { Children, Fragment, useEffect, useState } from "react";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";
import "./BudgetTrackingScreen.css";
import { TextHeaderComponents } from "../../components/TextHeaderComponents/TextHeaderComponents";
import { exportExcel } from "../../Helper/ExportExcel";
import {
  budgetFilter,
  GetPermissionReport,
  GetReportBudget,
  GetReportBudgetFilter,
  GetReportBudgetSummaries,
} from "../../Services/BudgetReportService";
import moment from "moment";
import { Button } from "primereact/button";

type DataNode = {
  key: string;
  type: string;
  desc: string;
  summary_alloc: number;
  summary_PreAlloc: number;
  summary_reserve: number;
  summary_used: number;
  summary_paid: number;
  summary_PreAllocRemainning: number;
  summary_remaining: number;
  memoId?: number;
  children: DataNode[];
};

type BudgetReportItem = {
  key: string;
  type: string;
  desc: string;
  summary_alloc: number;
  summary_PreAlloc: number;
  summary_reserve: number;
  summary_used: number;
  summary_paid: number;
  summary_PreAllocRemainning: number;
  summary_remaining: number;
  summary_cancelled: number;
  memoId?: number;
  children: BudgetReportItem[];
};

type FilterType = {
  label: string;
  value: any;
};

const Type = {
  BUDGET: "BUDGET",
  PR: "PR",
  PO: "PO",
  PV: "PV",
  GR: "GR",
};
/**
 * Extracts desc values only from items with type "IO" from nested children structure
 * @param data - Array of BudgetReportItem or single item
 * @returns Array of desc strings from items where type === "IO"
 */
function extractIODescriptions(data: BudgetReportItem[] | BudgetReportItem): string[] {
  const descriptions: string[] = [];

  function traverse(items: BudgetReportItem | BudgetReportItem[]) {
    if (Array.isArray(items)) {
      items.forEach((item) => {
        if (item.type === "IO" && item.desc) {
          descriptions.push(item.desc);
        }
        if (item.children && item.children.length > 0) {
          traverse(item.children);
        }
      });
    } 
  }

  traverse(data);
  return descriptions;
}

function renderDescription(value: any, record: any) {
  let val = record.memoId ? (
    <Link
      className="text-form-running"
      target="_blank"
      to={`/Request?MemoID=${record.memoId}`}
    >
      {value}
    </Link>
  ) : (
    value
  );
  switch (record.type) {
    case Type.BUDGET:
      return <b style={{ color: "#282f6a" }}>{val}</b>;
    case Type.PR:
      return <b>{val}</b>;
    case Type.PV:
      return <span style={{ color: "grey" }}>{val}</span>;
    case Type.GR:
      return <i style={{ color: "grey" }}>{val}</i>;
    default:
      return val;
  }
}

function renderNumber(value: number, record: any, index: number, options: any) {
   let val =
    value !== null && value !== undefined
      ? value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "-";
  let view = null;
  let color = "#282f6a";
  if (options.col == "remaining") {
    let prefix = options.prefix;
    let alloc = record[`${prefix}_alloc`];
    let remain = record[`${prefix}_remaining`];
    if (alloc && remain) {
      let percentage = remain / alloc;
      if (percentage < 0.1) {
        color = "#f00";
      } else if (percentage < 0.2) {
        color = "#f80";
      }
    }
  }
  switch (record.type) {
    case Type.BUDGET:
      view = <b style={{ color: color }}>{val}</b>;
      break;
    case Type.PR:
      view = <b>{val}</b>;
      break;
    case Type.PV:
      view = <i style={{ color: "grey" }}>{val}</i>;
      break;
    default:
      view = val;
  }
  return <div style={{ textAlign: "right", width: "100%" }}>{view}</div>;
}

function genBudgetColumn(prefix = "", options = {}) {
  const budgetColumn: ColumnsType<any> = [
    {
      title: "งบประมาณที่ตั้ง",
      dataIndex: `${prefix}_alloc`,
      key: `${prefix}_alloc`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "alloc" }),
    },
    {
      title: "ขออนุมัติใช้",
      dataIndex: `${prefix}_PreAlloc`,
      key: `${prefix}_PreAlloc`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "PreAlloc" }),
    },
    {
      title: "วงเงินจัดหา",
      dataIndex: `${prefix}_reserve`,
      key: `${prefix}_reserve`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "col" }),
    },
    {
      title: "วงเงินสั่งซื้อ/สั่งจ้าง",
      dataIndex: `${prefix}_used`,
      key: `${prefix}_used`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "used" }),
    },
    {
      title: "ค่าใช้จ่ายเกิดจริง",
      dataIndex: `${prefix}_paid`,
      key: `${prefix}_paid`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, { prefix: prefix, col: "paid" }),
    },
    {
      title: "ขออนุมัติใช้คงเหลือ",
      dataIndex: `${prefix}_PreAllocRemainning`,
      key: `${prefix}_PreAllocRemainning`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, {
          prefix: prefix,
          col: "PreAllocRemainning",
        }),
    },
    {
      title: "ที่ตั้งไว้คงเหลือ",
      dataIndex: `${prefix}_remaining`,
      key: `${prefix}_remaining`,
      width: 220,
      align: "center",
      ...options,
      render: (value, record, index) =>
        renderNumber(value, record, index, {
          prefix: prefix,
          col: "remaining",
        }),
    },
  ];
  return budgetColumn;
}

function BudgetSummary() {
  const columns: ColumnsType<any> = [
    {
      title: (
        <div style={{ textAlign: "center" }}>รายการเอกสารที่เกี่ยวข้อง</div>
      ),
      dataIndex: "desc",
      key: "desc",
      width: 300,
      fixed: "left",
      render: (value, record, index) => renderDescription(value, record),
    },
    {
      title: "รายการสรุปงบประมาณค่าใช้จ่าย",
      key: "key",
      children: genBudgetColumn("summary"),
    },
  ];

  const [data, setData] = useState<BudgetReportItem[]>([]);
  const [originalData, setOriginalData] = useState<BudgetReportItem[]>([]);
  const [filteredData, setFilteredData] = useState<BudgetReportItem[]>([]);
  const [filters, setFilters] = useState<{
    cost: FilterType[];
    gl: FilterType[];
    io: FilterType[];
    year: FilterType[];
    gl_name: FilterType[];
  }>({
    cost: [],
    gl: [],
    io: [],
    year: [],
    gl_name: [],
  });
  const [originalFilters, setOriginalFilters] = useState<{
    cost: FilterType[];
    gl: FilterType[];
    io: FilterType[];
    year: FilterType[];
    gl_name: FilterType[];
  }>({
    cost: [],
    gl: [],
    io: [],
    year: [],
    gl_name: [],
  });
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [form] = Form.useForm();
  const [showExportSum, setShowExportSum] = useState(false);
  const [pageSize ,setPageSize] = useState<number>(10); 

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const year = moment().get("year");

      await getReport();
      await getReportFilter(year);
      setLoading(false);
    };
    fetchData();
  }, []);

  const getReport = async (
    filterVal: budgetFilter = {
      budgetYear: moment().get("year"),
      description: "",
      costCenter: "",
      glAccount: "",
      internalOrder: "",
      glName: "",
    },
    pageSize: number = 1
  ) => {
    
    const data: { totalCount: number; reportData: BudgetReportItem[] } =
      await GetReportBudget({
        pageNumber: pageSize,
        pageSize: 10,
        filter: filterVal,
      });

    console.log(data);
      if(data){
      setTotal(data.totalCount);
      setData(data.reportData);
      setFilteredData(data.reportData);
      setOriginalData(data.reportData);
      setCurrentPage(pageSize);
    }
  };

const getReportFilter = async (budgetYear: number) => {
  const resFilter = await GetReportBudgetFilter(budgetYear);
  setFilters(resFilter);
  setOriginalFilters(resFilter);
};




  const filterFunc = async (changedValues: any, allValues: any) => {
    const year = moment(allValues.budgetYear).get("year");

    setLoading(true);

    if (changedValues["budgetYear"]) {
      await getReport({
        ...allValues,
        budgetYear: year,
      });

      await getReportFilter(year);

      setLoading(false);
      return;
    }

    const filteredResult = originalData
      ?.map((item) =>
        deepFilterTree(item, {
          description: allValues.description || "",
          costCenter: allValues.costCenter || "",
          glName: allValues.glName || "",
          internalOrder: allValues.internalOrder || ""
        })
      )
      .filter((e): e is BudgetReportItem => e !== null);
    const ioDescriptions = extractIODescriptions(filteredResult);

    // console.log(filteredResult, ioItems, ioDescriptions, filters, "testqq");
    setFilters(originalFilters.io.length > 0 ? {
      ...originalFilters,
      io: originalFilters.io.filter((io) => ioDescriptions.includes(io.label)),
    } : originalFilters);
    setFilteredData(filteredResult);
    setLoading(false);
  };

  function deepFilterTree(
    items: BudgetReportItem,
    filters: {
      description?: string;
      costCenter?: string;
      glName?: string;
      internalOrder?: string;
    }
  ): BudgetReportItem | null {
    const descText = items.desc || "";

    const {
      description = "",
      costCenter = "",
      glName = "",
      internalOrder = "",
    } = filters;

    // filter COST, GL, IO
    if (items.type === "COST" && costCenter && items.desc !== costCenter) {
      return null;
    }

    if (items.type === "GL" && glName && items.desc !== glName) {
      return null;
    }

    if (items.type === "IO" && internalOrder && items.desc !== internalOrder) {
      return null;
    }

    // search description
    const matchDescription =
      !description ||
      descText.toLowerCase().includes(description.toLowerCase());

    if (matchDescription && description) {
      return {
        ...items,
        children: items.children || [],
      };
    }

    // filter children
    const filterChild = (items.children || [])
      .map((child) => deepFilterTree(child, filters))
      .filter((c): c is BudgetReportItem => c !== null)
      .sort((a, b) => b.summary_alloc - a.summary_alloc);


    // show data filter
    if (filterChild.length === 0 && description) {
      return null;
    }

    return {
      ...items,
      children: filterChild,
    };
  }

  // function filterItemWithDescription(
  //   item: BudgetReportItem,
  //   descKeyword: string
  // ): BudgetReportItem | null {
  //   const hasMatchingDescription =
  //     descKeyword && item.desc && item.desc.includes(descKeyword);

  //   const filteredChildren = item.children
  //     ?.map((child) => filterItemWithDescription(child, descKeyword))
  //     .filter((child): child is BudgetReportItem => child !== null);

  //   if (hasMatchingDescription || filteredChildren?.length > 0) {
  //     return {
  //       key: item.key,
  //       desc: item.desc,
  //       memoId: item.memoId,
  //       summary_alloc: item.summary_alloc,
  //       summary_remaining: item.summary_remaining,
  //       summary_paid: item.summary_paid,
  //       summary_reserve: item.summary_reserve,
  //       summary_used: item.summary_used,
  //       summary_cancelled: item.summary_cancelled,
  //       summary_PreAlloc: item.summary_PreAlloc,
  //       summary_PreAllocRemainning: item.summary_PreAllocRemainning,
  //       type: item.type,
  //       children: hasMatchingDescription ? item.children : filteredChildren,
  //     };
  //   }

  //   return null;
  // }

  // function filterReport(
  //   data: BudgetReportItem[],
  //   costKey: string,
  //   glKey: string,
  //   ioKey: string,
  //   glNameKey: string
  // ): BudgetReportItem[] {
  //   return data
  //     ?.filter((item) => !costKey || (item.desc && item.desc.includes(costKey)))
  //     .map((item) => {
  //       item.children = item.children
  //         ?.filter(
  //           (gl) =>
  //             (!glKey || (gl.desc && gl.desc.includes(glKey))) &&
  //             (!glNameKey || (gl.desc && gl.desc.includes(glNameKey)))
  //         )
  //         .map((gl) => {
  //           gl.children = gl.children
  //             .filter((io) => !ioKey || (io.desc && io.desc.includes(ioKey)))
  //             .sort((a, b) => b.summary_alloc - a.summary_alloc);

  //           return gl;
  //         })
  //         .filter((gl) => gl.children?.length > 0 || !ioKey)
  //         .sort((a, b) => b.summary_alloc - a.summary_alloc);

  //       return item;
  //     })
  //     .filter((item) => item.children?.length > 0 || (!glKey && !ioKey))
  //     .sort((a, b) => b.summary_alloc - a.summary_alloc);
  // }

  const flattenData = (data: BudgetReportItem[], parentKey: string = "") => {
    let result: any[] = [];
    if (data) {
      data.forEach((node) => {
        const flatNode = {
          Description: node.desc,
          Allocated: node.summary_alloc.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          Reserved: node.summary_reserve.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          Used: node.summary_used.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          Paid: node.summary_paid.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          Remaining: node.summary_remaining.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          // PreAlloc: node.summary_PreAlloc,
          // PreAllocRemainning: node.summary_PreAllocRemainning,
        };

        result.push(flatNode);

        if (node.children && node.children.length > 0) {
          const children = flattenData(node.children, node.key);
          result = result.concat(children);
        }
      });
    }

    return result;
  };

  return (
    <Row
      style={{ padding: "1rem" }}
      justify="space-between"
      align="middle"
      gutter={[10, 10]}
    >
      <Col span={24}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <Form form={form} layout="vertical" onValuesChange={filterFunc}>
              <Row justify="start" align="middle" gutter={[16, 0]}>
                {/* Budget Year and Date Picker */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle">
                    <p
                      style={{
                        fontSize: " 13px",
                        lineHeight: "15px",
                        marginBottom: "5px",
                        color: "#262A2D",
                        fontWeight: 700,
                      }}
                    >
                      ปีงบประมาณ{" "}
                      <span
                        style={{
                          color: "red",
                        }}
                      >
                        *
                      </span>
                    </p>
                  </Row>
                  <Form.Item name="budgetYear" initialValue={moment()}>
                    <DatePicker
                      picker="year"
                      style={{ width: "100%" }}
                      allowClear={false}
                    />
                  </Form.Item>
                </Col>

                {/* Description and Input */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle">
                    <p
                      style={{
                        fontSize: " 13px",
                        lineHeight: "15px",
                        marginBottom: "5px",
                        color: "#262A2D",
                        fontWeight: 700,
                      }}
                    >
                      รายการเอกสารที่เกี่ยวข้อง
                    </p>
                  </Row>

                  <Form.Item name="description">
                    <Input placeholder="ค้นหารายการเอกสารที่เกี่ยวข้อง" />
                  </Form.Item>
                </Col>

                {/* Cost Center */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle">
                    <p
                      style={{
                        fontSize: " 13px",
                        lineHeight: "15px",
                        marginBottom: "5px",
                        color: "#262A2D",
                        fontWeight: 700,
                      }}
                    >
                      สายงาน/สถาบัน
                    </p>
                  </Row>

                  <Form.Item name="costCenter">
                    <Select
                      options={filters.cost}
                      placeholder="เลือกสายงาน/สถาบัน"
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>

                {/* Account Name */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle">
                    <p
                      style={{
                        fontSize: " 13px",
                        lineHeight: "15px",
                        marginBottom: "5px",
                        color: "#262A2D",
                        fontWeight: 700,
                      }}
                    >
                      ชื่อหมวดหมู่บัญชี
                    </p>
                  </Row>

                  <Form.Item name="glName">
                    <Select
                      options={filters.gl_name}
                      placeholder="เลือกชื่อหมวดหมู่บัญชี"
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>

                {/* internalOrder */}
                <Col xs={24} sm={12} md={8}>
                  <Row align="middle">
                    <p
                      style={{
                        fontSize: " 13px",
                        lineHeight: "15px",
                        marginBottom: "5px",
                        color: "#262A2D",
                        fontWeight: 700,
                      }}
                    >
                      ชื่อโครงการ/สินทรัพย์ถาวร
                    </p>
                  </Row>

                  <Form.Item name="internalOrder">
                    <Select
                      options={filters.io}
                      placeholder="เลือกชื่อโครงการ/สินทรัพย์ถาวร"
                      style={{ width: "100%" }}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
          <Col xs={24} md={4}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <Button
                label="Export to excel"
                icon="pi pi-file-excel"
                data-pr-tooltip="XLS"
                loading={loading}
                style={{
                  height: "38px",
                  width: "100%",
                  background: " #28a745",
                  border: "1px solid #28a745",
                  color: "#ffffff",
                  fontSize: "1em",
                  borderRadius: "6px",
                  margin: " 0px 11px 0px 0px",
                  justifyContent: "center",
                }}
                onClick={async () => {
                  await exportExcel([],"BudgetSummary", flattenData(filteredData));
                }}
              />
              <Button
                label="Export Budget Summaries"
                icon="pi pi-file-excel"
                data-pr-tooltip="XLS"
                loading={loading}
                style={{
                  height: "38px",
                  width: "100%",
                  background: " #28a745",
                  border: "1px solid #28a745",
                  color: "#ffffff",
                  fontSize: "1em",
                  borderRadius: "6px",
                  margin: " 0px 11px 0px 0px",
                  justifyContent: "center",
                }}
                onClick={async () => {
                  setLoading(true);
                  let value = form.getFieldsValue();
                  let bodyData: any = {
                    ...value,
                    budgetYear: moment(value.budgetYear).get("year"),
                  };
                  const res = await GetReportBudgetSummaries(bodyData);
                  console.log(res);
                  if (res.Data) {
                    await exportExcel([],"BudgetSummaries", res.Data);
                  }
                  setLoading(false);

                  // value.budgetYear = value.budgetYear.get("year");
                  // getReport(value);
                }}
              />
            </div>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row justify="end" gutter={[10, 10]}>
          <Col span={24}>
            <Table
              className="budget-table-tracking"
              columns={columns}
              dataSource={filteredData}
              loading={loading}
              rowKey={"key"}
              bordered
              size="small"
              style={{ width: "100%" }}
              scroll={{ x: "max-content" }}
              onChange={(pagination) => {
                let value = form.getFieldsValue();
                value.budgetYear = value.budgetYear.get("year");
                console.log(pagination);
                pagination.pageSize && setPageSize(pagination.pageSize)
                // getReport(value, pagination.current);
              }}
              pagination={{ pageSize: pageSize, total: total }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
}

export default BudgetSummary;
